/**
 * Pure helpers for the workspace dashboard's "Assigned to me" widget
 * (operator-task-fe-comment-assignment-make-useful): the OPEN record comments
 * assigned to the viewer, newest first, each linking to the comment.
 */

/** Filter condition list: open comments assigned to one identity. */
const openAssignedConditions = (identityId: string) => [
    { field: "assignee_identity_id", op: "eq", value: identityId },
    { field: "resolved_at", op: "unset" },
];

/** Filter for the open comments assigned to one identity. */
export const buildAssignedCommentsFilter = (identityId: string): string =>
    JSON.stringify({ and: openAssignedConditions(identityId) });

/**
 * Query suffix (`&tab=…&customFilter=…`, appended to the Advanced Search path) to every record that has an open comment
 * assigned to the identity — a "Has related record" (exists) filter on the
 * record_comment relation. The filter rides in the URL rather than the stored
 * filter, so it cannot clobber (or be clobbered by) sibling dashboard links that
 * write the stored Advanced Search filter at render time. `resourcesFilter` is the
 * Resources tab's base (resource-type) filter: a deep link carrying only a
 * customFilter would leave the Type filter empty ("none") and match nothing.
 */
export const buildAssignedCommentsSearchQuery = (
    identityId: string,
    resourcesFilter: string
): string => {
    const customFilter = JSON.stringify({
        and: [
            {
                exists: {
                    relation: "record_comment",
                    where: { and: openAssignedConditions(identityId) },
                },
            },
        ],
    });
    return `&tab=resources&filter=${encodeURIComponent(
        resourcesFilter
    )}&customFilter=${encodeURIComponent(customFilter)}`;
};

/**
 * A one-line plain-text snippet of a markdown comment body: markdown syntax
 * (headings, emphasis, code ticks, link targets, list bullets) stripped,
 * whitespace collapsed, and cut at `max` characters with an ellipsis.
 */
export const commentSnippet = (body: string | null | undefined, max = 120): string => {
    const text = (body ?? "")
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
        .replace(/^\s{0,3}(#{1,6}|[-*+]|\d+\.|>)\s+/gm, "")
        .replace(/[*_`~]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
};
